"use client";

import Link from "next/link";
import { trackFunnel } from "@/libs/analytics";
import FadeCyclePreview from "./FadeCyclePreview";
import BorderFrameCyclePreview, { type BorderFrame } from "./BorderFrameCyclePreview";

export default function CatalogLookCard({
  href,
  title,
  tagline,
  images,
  toolId,
  cardType,
  cardId,
  section,
  overlaySrc,
  borderFrames,
}: {
  href: string;
  title: string;
  tagline?: string;
  images: string[];
  toolId: string;
  cardType: "look" | "style";
  cardId: string;
  section: string;
  /** Demo cutout layered on fade-cycle treatments (free-tool cards). */
  overlaySrc?: string;
  /** Circular Borders & Frames cycle — same rings as Free Studio. */
  borderFrames?: BorderFrame[];
}) {
  const frames = images.filter(Boolean);
  const rings = borderFrames?.filter((frame) => frame.color) ?? [];
  const showRings = rings.length > 0 && Boolean(overlaySrc);

  return (
    <Link
      href={href}
      onClick={() =>
        trackFunnel("tool_engagement", "dashboard_card_clicked", {
          tool_id: toolId,
          card_type: cardType,
          card_id: cardId,
          section,
          destination_href: href,
        })
      }
      className="group flex h-full flex-col"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-2xl bg-gray-100">
        {showRings ? (
          <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-105">
            <BorderFrameCyclePreview frames={rings} cutoutSrc={overlaySrc!} />
          </div>
        ) : frames.length > 0 ? (
          <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-105">
            <FadeCyclePreview images={frames} maxFrames={frames.length} alt={title} className="h-full w-full" />
            {overlaySrc ? (
              <img
                src={overlaySrc}
                alt=""
                className="absolute inset-0 h-full w-full object-contain object-bottom pointer-events-none"
                draggable={false}
              />
            ) : null}
          </div>
        ) : (
          <div className="absolute inset-0 bg-gray-100" />
        )}
      </div>
      <div className="pt-3">
        <h3 className="text-base font-bold leading-tight text-gray-900">{title}</h3>
        {tagline ? (
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-500">{tagline}</p>
        ) : null}
      </div>
    </Link>
  );
}
